import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import type { Paginated, SolicitudResponse } from '../../../api/types';
import { usersKeys } from '../../users/api/keys';
import { solicitudesKeys } from './keys';

export interface SolicitudesListParams extends Record<string, unknown> {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
}

export function useSolicitudesList(params: SolicitudesListParams = {}) {
  return useQuery<Paginated<SolicitudResponse>>({
    queryKey: solicitudesKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<SolicitudResponse>>('/admin/solicitudes', {
        params,
      });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useApproveSolicitud() {
  const queryClient = useQueryClient();
  return useMutation<SolicitudResponse, Error, { id: number; roleIds: string[] }>({
    mutationFn: async ({ id, roleIds }) => {
      const { data } = await apiClient.post<SolicitudResponse>(`/admin/solicitudes/${id}/approve`, {
        roleIds,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useRejectSolicitud() {
  const queryClient = useQueryClient();
  return useMutation<SolicitudResponse, Error, { id: number; motivo: string }>({
    mutationFn: async ({ id, motivo }) => {
      const { data } = await apiClient.post<SolicitudResponse>(`/admin/solicitudes/${id}/reject`, {
        reason: motivo,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.lists() });
    },
  });
}
